import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getEvents } from '../services/eventsService';
import { getHistorySettings, updateHistorySetting } from '../services/historyService';
import { getDisplayErrorMessage } from '../services/apiError';
import TimelineItem from './TimelineItem';
import EventDetails from './EventDetails';
import { TIMELINE_CONFIG } from './constants';
import RichTextDisplay from './RichTextDisplay';
import ErrorMessage from './ErrorMessage';

const Timeline = ({ history, onDateFetched, isOwner, studyState, onStudyAnswer, isSubmitting }) => {
  const historyId = history.id;
  const [events, setEvents] = useState([]);
  const [timelineWidth, setTimelineWidth] = useState(0);
  const [pixelsPerPoint, setPixelsPerPoint] = useState(0);
  const [resolution, setResolution] = useState(TIMELINE_CONFIG.POINTS_PER_SCREEN);
  const [timelineData, setTimelineData] = useState(null);
  const [resolutionLimits, setResolutionLimits] = useState({});
  const [error, setError] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [localSelectedEventId, setLocalSelectedEventId] = useState(null);
  
  const isStudyMode = !!studyState;
  const selectedEventId = isStudyMode ? localSelectedEventId : searchParams.get('eventId');
  const navigate = useNavigate();
  const scrollContainer = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, scrollLeft: 0 });

  const calculateTimelineConfig = (timelineLength, containerWidth, pointsPerScreen) => {
    if (!timelineLength) return null;

    const PADDING_HORIZONTAL = 40;
    const calculatedPixelsPerPoint = containerWidth / pointsPerScreen;
    const calculatedWidth = timelineLength * calculatedPixelsPerPoint;
    const totalWidth = Math.max(calculatedWidth, containerWidth) + PADDING_HORIZONTAL;

    return {
      width: totalWidth,
      pixelsPerPoint: calculatedPixelsPerPoint
    };
  };

  const processEvents = (sortedEvents, timelineLength, pixelsPerPoint) => {
    const PADDING_LEFT = 20;
    
    const positionedEvents = sortedEvents.map(event => {
      const startPoint = event.timelinePoints[0];
      const endPoint = event.timelinePoints[event.timelinePoints.length - 1];

      const leftPosition = startPoint * pixelsPerPoint + PADDING_LEFT;
      const timeBasedWidth = (endPoint - startPoint) * pixelsPerPoint;

      return { 
        ...event, 
        track: event.timelineOrder - 1, 
        leftPosition, 
        adjustedCardWidth: timeBasedWidth,
        timeBasedWidth,
        startLabelOffset: 0,
        endLabelOffset: 0,
        hideStartDate: false,
        hideEndDate: false
      };
    });

    const estimateDateLabelWidth = (dateText) => {
      const length = dateText?.length || 0;
      const width = length * TIMELINE_CONFIG.DATE_CHAR_WIDTH + TIMELINE_CONFIG.DATE_LABEL_PADDING;
      return Math.max(TIMELINE_CONFIG.MIN_DATE_LABEL_WIDTH, width);
    };

    const labelCandidates = [];
    const seenLabels = new Set();

    const hideLabel = (eventIndex, type) => {
      if (type === 'start') {
        positionedEvents[eventIndex].hideStartDate = true;
      } else {
        positionedEvents[eventIndex].hideEndDate = true;
      }
    };

    const addLabel = (eventIndex, type, date, x) => {
      const key = `${date}|${Math.round(x * 10) / 10}`;
      if (seenLabels.has(key)) {
        hideLabel(eventIndex, type);
        return;
      }

      seenLabels.add(key);
      labelCandidates.push({
        eventIndex,
        type,
        x,
        width: estimateDateLabelWidth(date)
      });
    };

    positionedEvents.forEach((event, index) => {
      const startDate = event.dates?.[0];
      if (startDate) {
        addLabel(index, 'start', startDate, event.leftPosition);
      }

      const isRangeEvent = event.timelinePoints?.length > 1;
      const endDate = isRangeEvent ? event.dates?.[event.dates.length - 1] : null;
      if (isRangeEvent && endDate) {
        addLabel(index, 'end', endDate, event.leftPosition + (event.timeBasedWidth || 0));
      }

      const nextOnTrack = positionedEvents.slice(index + 1).find(e => e.track === event.track);
      if (nextOnTrack) {
        event.distanceToNextOnTrack = nextOnTrack.leftPosition - event.leftPosition;
      }
    });
    
    const levels = [];

    const applyOffset = (candidate, levelIndex) => {
      const offset = levelIndex * TIMELINE_CONFIG.DATE_LABEL_STACK_OFFSET;
      if (candidate.type === 'start') {
        positionedEvents[candidate.eventIndex].startLabelOffset = offset;
      } else {
        positionedEvents[candidate.eventIndex].endLabelOffset = offset;
      }
    };

    labelCandidates
      .sort((a, b) => a.x - b.x)
      .forEach((candidate) => {
        let placed = false;

        for (let levelIndex = 0; levelIndex < levels.length; levelIndex += 1) {
          const level = levels[levelIndex];
          const minDistance = (level.lastWidth + candidate.width) / 2 + TIMELINE_CONFIG.MIN_DATE_LABEL_GAP;
          const isOverlapping = (candidate.x - level.lastX) < minDistance;

          if (!isOverlapping) {
            level.lastX = candidate.x;
            level.lastWidth = candidate.width;
            applyOffset(candidate, levelIndex);
            placed = true;
            break;
          }
        }

        if (!placed) {
          const newLevelIndex = levels.length;
          levels.push({ lastX: candidate.x, lastWidth: candidate.width });
          applyOffset(candidate, newLevelIndex);
        }
      });

    return positionedEvents;
  };

  useEffect(() => {
    setEvents([]);

    const fetchEvents = async (historyId) => {
      setError(null);

      try {
        const settings = await getHistorySettings(historyId);
        
        let savedResolution = null;
        let rules = null;
        
        if (settings) {
          for (const section of settings.sections) {
            const field = section.fields.find(f => f.key === 'timeline_resolution');
            if (field) {
              savedResolution = field.value;
              if (field.rules) {
                const parsedRules = {};
                const ruleParts = field.rules.split('|');
                for (let i = 0; i < ruleParts.length; i++) {
                  const [name, value] = ruleParts[i].split(':');
                  if (name && value) {
                    parsedRules[name] = parseInt(value, 10);
                  }
                }
                rules = parsedRules;
              }
              break;
            }
          }
        }
        
        if (rules && rules.min !== undefined && rules.max !== undefined) {
          setResolutionLimits({ min: rules.min, max: rules.max });
        }

        const currentResolution = savedResolution || TIMELINE_CONFIG.POINTS_PER_SCREEN;
        setResolution(currentResolution);

        const data = await getEvents(historyId);
        const eventsList = data.events || data;
        const eventsWithHistoryId = eventsList.map(event => ({ ...event, historyId }));
        const sortedEvents = [...eventsWithHistoryId].sort((a, b) => (a.timelinePoints?.[0] || 0) - (b.timelinePoints?.[0] || 0));

        const containerWidth = scrollContainer.current ? scrollContainer.current.clientWidth : 0;
        const timelineLength = data.timeline ? data.timeline.length : 20;
        
        setTimelineData({ sortedEvents, timelineLength });

        const timelineConfig = calculateTimelineConfig(timelineLength, containerWidth, currentResolution);

        if (!timelineConfig) {
          if (onDateFetched) onDateFetched({ first: 'No events for this history.', last: '' });
          return;
        }

        const processedEvents = processEvents(sortedEvents, timelineLength, timelineConfig.pixelsPerPoint);
        setEvents(processedEvents);

        if (sortedEvents.length > 0 && onDateFetched) {
          onDateFetched({ first: sortedEvents[0].dates[0], last: sortedEvents[sortedEvents.length - 1].dates[sortedEvents[sortedEvents.length - 1].dates.length - 1] });
        }
        setTimelineWidth(timelineConfig.width);
        setPixelsPerPoint(timelineConfig.pixelsPerPoint);
      } catch (err) {
        setError(getDisplayErrorMessage(err));
        setEvents([]);
        setTimelineWidth(0);
        setPixelsPerPoint(0);
        setTimelineData(null);
        if (onDateFetched) onDateFetched({ first: '', last: '' });
      }
    };

    if (historyId) {
      fetchEvents(historyId);
    } else {
      setEvents([]);
      setTimelineWidth(0);
      setPixelsPerPoint(0);
      setTimelineData(null);
      if (onDateFetched) onDateFetched({ first: '', last: '' });
    }
  }, [historyId, onDateFetched]);

  useEffect(() => {
    if (!timelineData || !scrollContainer.current) return;

    const { sortedEvents, timelineLength } = timelineData;
    const containerWidth = scrollContainer.current.clientWidth;
    const timelineConfig = calculateTimelineConfig(timelineLength, containerWidth, resolution);

    if (!timelineConfig) return;

    const processedEvents = processEvents(sortedEvents, timelineLength, timelineConfig.pixelsPerPoint);
    
    setEvents(processedEvents);
    setTimelineWidth(timelineConfig.width);
    setPixelsPerPoint(timelineConfig.pixelsPerPoint);
  }, [resolution, timelineData]);

  useEffect(() => {
    if (scrollContainer.current && events.length > 0) {
      const targetEvent = selectedEventId ? events.find(event => String(event.id) === selectedEventId) : events[0];
      if (targetEvent) {
        const targetPosition = targetEvent.leftPosition;
        const containerWidth = scrollContainer.current.clientWidth;
        const scrollLeft = Math.max(0, targetPosition - (containerWidth / 2));
        scrollContainer.current.scrollLeft = scrollLeft;
      }
    }
  }, [events, selectedEventId]);

  useEffect(() => {
    if (isStudyMode && studyState?.eventId) {
      setLocalSelectedEventId(studyState.eventId);
    }
  }, [isStudyMode, studyState?.eventId]);
  
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.pageX - scrollContainer.current.offsetLeft,
      scrollLeft: scrollContainer.current.scrollLeft
    });
    scrollContainer.current.style.cursor = 'grabbing';
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (scrollContainer.current) {
      scrollContainer.current.style.cursor = 'grab';
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    e.preventDefault();
    const x = e.pageX - scrollContainer.current.offsetLeft;
    const walk = (x - dragStart.x) * TIMELINE_CONFIG.DRAG_SPEED_MULTIPLIER;
    scrollContainer.current.scrollLeft = dragStart.scrollLeft - walk;
  };

  const handleEventClick = (event) => {
    if (isStudyMode) {
      const currentEventId = localSelectedEventId;
      
      if (currentEventId && String(currentEventId) === String(event.id)) {
        setLocalSelectedEventId(null);
      } else {
        setLocalSelectedEventId(event.id);
      }
    } else {
      const newParams = new URLSearchParams(searchParams);
      const currentEventId = selectedEventId;
      
      if (currentEventId && Number(currentEventId) === event.id) {
        newParams.delete('eventId');
      } else {
        newParams.set('eventId', event.id);
      }
      
      setSearchParams(newParams);
    }
  };

  const handleAddEventClick = () => {
    navigate(`/createEvent/${historyId}`);
  };

  const handleEditHistoryClick = () => {
    navigate(`/editHistory/${historyId}`, { state: { history } });
  };

  const handleEventDeleted = (eventId) => {
    setEvents(prevEvents => {
      const deletedIndex = prevEvents.findIndex(e => e.id === eventId);
      if (deletedIndex === -1) return prevEvents;

      const newEvents = prevEvents.filter(e => e.id !== eventId);

      if (String(selectedEventId) === String(eventId)) {
        const newSelectedEvent = newEvents[Math.max(0, deletedIndex - 1)];

        if (isStudyMode) {
          setLocalSelectedEventId(newSelectedEvent ? newSelectedEvent.id : null);
        } else {
          const newParams = new URLSearchParams(searchParams);
          if (newSelectedEvent) {
            newParams.set('eventId', newSelectedEvent.id);
          } else {
            newParams.delete('eventId');
          }
          setSearchParams(newParams);
        }
      }

      return newEvents; 
    })
  };

  const selectedEvent = events.find(event => String(event.id) === selectedEventId);

  const applyStudyModifications = (eventsList) => {
    if (!isStudyMode || !studyState?.eventsModification) {
      return eventsList;
    }

    if (studyState.status === 'mastered') {
      return eventsList;
    }

    const result = [];
    for (let i = 0; i < eventsList.length; i++) {
      const event = eventsList[i];
      let modification = null;
      
      for (let j = 0; j < studyState.eventsModification.length; j++) {
        if (studyState.eventsModification[j].id === event.id) {
          modification = studyState.eventsModification[j];
          break;
        }
      }
      
      if (modification) {
        result.push({
          ...event,
          modification,
          studyStatus: studyState.status
        });
      } else {
        result.push(event);
      }
    }
    
    return result;
  };

  const processedEvents = applyStudyModifications(events);
  const selectedProcessedEvent = processedEvents.find(event => String(event.id) === selectedEventId);

  useEffect(() => {
    const metaDescription = document.querySelector('meta[name="description"]');
    const truncateText = (text, maxLength) => {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    };

    if (selectedEvent) {
      document.title = `Timelined - ${history.name} - ${selectedEvent.name}`;
      if (metaDescription) {
        const eventDescription = `${history.description || ''}. ${selectedEvent.shortDescription || ''}`;
        metaDescription.setAttribute('content', truncateText(eventDescription, 240));
      }
    } else if (history) {
      document.title = `Timelined - ${history.name}`;
      if (metaDescription) {
        metaDescription.setAttribute('content', truncateText(history.description, 240));
      }
    }
  }, [history, selectedEvent]);

  const handleNavigationClick = (destination) => {
    if (!events.length) return;

    const currentIndex = events.findIndex(e => String(e.id) === String(selectedEventId));
    let targetIndex = null;

    switch (destination) {
      case 'first':
        targetIndex = events[0];
        break;
      case 'previous':
        if (currentIndex > 0) {
          targetIndex = events[currentIndex - 1];
        } else if (currentIndex === -1) {
          targetIndex = events[0];
        }
        break;
      case 'next':
        if (currentIndex !== -1 && currentIndex < events.length - 1) {
          targetIndex = events[currentIndex + 1];
        } else if (currentIndex === -1) {
          targetIndex = events[0];
        }
        break;
      case 'last':
        targetIndex = events[events.length - 1];
        break;
      default:
        break;
    }

    if (targetIndex && String(targetIndex.id) !== String(selectedEventId)) {
      if (isStudyMode) {
        setLocalSelectedEventId(targetIndex.id);
      } else {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('eventId', targetIndex.id);
        setSearchParams(newParams);
      }
    }
  };

  const handleZoom = async (direction) => {
    const newResolution = direction === 'in'
      ? Math.max(resolutionLimits.min, Math.round(resolution * TIMELINE_CONFIG.ZOOM_IN_FACTOR))
      : Math.min(resolutionLimits.max, Math.round(resolution * TIMELINE_CONFIG.ZOOM_OUT_FACTOR));
    
    setResolution(newResolution);
    
    if (historyId && isOwner) {
      try {
        await updateHistorySetting(historyId, 'timeline_resolution', newResolution);
      } catch (err) {
        setError(getDisplayErrorMessage(err));
      }
    }
  };

  return (
    <div className="w-full h-screen bg-gray-100 flex flex-col relative">
      <ErrorMessage message={error} onDismiss={() => setError(null)} />
      <div className="text-sm text-gray-600 my-4 px-14 overflow-x-auto max-h-64">
        {history.description ? (
          <RichTextDisplay content={history.description} />
        ) : (
          <span className="text-gray-400">This history has no description</span>
        )}
      </div>
      {isOwner && (
      <button
        onClick={handleEditHistoryClick}
        className="bg-white w-8 h-8 rounded-full z-50 absolute right-2 top-2 flex items-center justify-center shadow-lg hover:bg-blue-100 hover:border-2 hover:border-blue-500"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-gray-700">
          <path d="M5.433 13.917l1.262-3.155A4 4 0 017.58 9.42l6.92-6.918a2.121 2.121 0 013 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 01-.65-.65z" />
          <path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0010 3H4.75A2.75 2.75 0 002 5.75v9.5A2.75 2.75 0 004.75 18h9.5A2.75 2.75 0 0017 15.25V10a.75.75 0 00-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5z" />
        </svg>
      </button>
      )}

      <EventDetails event={selectedProcessedEvent} isOwner={isOwner} onDelete={handleEventDeleted} allEvents={events} isStudyMode={isStudyMode} />
      
      {isStudyMode && studyState?.navigation?.buttons && (
        <div className="bg-white border-y border-gray-200 py-2 flex justify-center gap-4">
          {studyState.navigation.buttons.map(button => (
            <button
              key={button.value}
              onClick={() => onStudyAnswer(button.value)}
              disabled={isSubmitting}
              className="bg-white text-black shadow-lg border-2 px-4 py-2 rounded hover:bg-blue-100 hover:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center"
            >
              <span>{button.title}</span>
              {button.info && (
                <span className="text-xs italic text-gray-500">{button.info}</span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="relative">
        <div className="absolute left-1/2 top-2 transform -translate-x-1/2 flex space-x-3 z-10">
          <button 
            onClick={() => handleNavigationClick('first')}
            className="bg-white rounded-md p-1 shadow-lg border-2 border-white hover:bg-blue-100 hover:border-2 hover:border-blue-500"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="m18.75 4.5-7.5 7.5 7.5 7.5m-6-15L5.25 12l7.5 7.5" />
            </svg>
          </button>
          <button 
            onClick={() => handleNavigationClick('previous')}
            className="bg-white rounded-md p-1 shadow-lg border-2 border-white hover:bg-blue-100 hover:border-2 hover:border-blue-500"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </button>
          <button 
            onClick={() => handleNavigationClick('next')}
            className="bg-white rounded-md p-1 shadow-lg border-2 border-white hover:bg-blue-100 hover:border-2 hover:border-blue-500"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </button>
          <button 
            onClick={() => handleNavigationClick('last')}
            className="bg-white rounded-md p-1 shadow-lg border-2 border-white hover:bg-blue-100 hover:border-2 hover:border-blue-500"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="m5.25 4.5 7.5 7.5-7.5 7.5m6-15 7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>

        <div className="absolute left-2 top-2 flex space-x-2 z-10">
          <button 
            onClick={() => handleZoom('out')}
            disabled={resolution >= resolutionLimits.max}
            className="bg-white rounded-md p-1 shadow-lg border-2 border-white hover:bg-blue-100 hover:border-2 hover:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-white"
            title="Zoom out"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607ZM13.5 10.5h-6" />
            </svg>
          </button>
          <button 
            onClick={() => handleZoom('in')}
            disabled={resolution <= resolutionLimits.min}
            className="bg-white rounded-md p-1 shadow-lg border-2 border-white hover:bg-blue-100 hover:border-2 hover:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-white"
            title="Zoom in"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607ZM10.5 7.5v6m3-3h-6" />
            </svg>
          </button>
        </div>

        {isOwner && (
          <button 
            onClick={handleAddEventClick}
            className="bg-white text-2xl w-8 h-8 rounded-full z-50 absolute right-2 top-2 pb-1 flex items-center justify-center shadow-lg hover:bg-blue-100 hover:border-2 hover:border-blue-500"
          >
            +
          </button>
        )}
        <div
          ref={scrollContainer}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseUp}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          className="w-full overflow-x-scroll overflow-y-hidden no-scrollbar cursor-grab active:cursor-grabbing bg-gray-200 relative h-[300px] select-none"
        >
          <div 
            style={{ width: `${timelineWidth}px`, paddingLeft: '20px', paddingRight: '20px' }}
            className="relative flex items-end pt-10 pb-10 min-w-min h-full"
          >
            <div className="absolute left-0 bottom-[2.875rem] w-full h-px bg-gray-400" />

            {timelineWidth > 0 && processedEvents.map((event, index) => (
              <TimelineItem
                key={event.id || `${event.name}-${index}`}
                event={event}
                pixelsPerPoint={pixelsPerPoint}
                index={index}
                onClick={handleEventClick}
                isSelected={selectedProcessedEvent && selectedProcessedEvent.id === event.id}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};  

export default Timeline;